/* eslint-disable @typescript-eslint/no-misused-promises */
import {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {z} from "zod";
import {SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Alert, Box, Grid, Paper, Stack, Typography} from "@mui/material";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {confirmUserResetPassword} from "../../services/authenticationService";
import {getResourceErrorMessage, isResourceFailed, isResourceLoading} from "../../utils/resourceUtils";
import {swallowException} from "../../utils/appUtils";
import {ActionButton} from "../base/ActionButton";
import {LabeledInput} from "../base/LabeledInput";
import {AppLayout} from "../AppLayout";
import {EmptyHeader} from "../EmptyHeader";
import {TextInput} from "../base/TextInput";

export const PasswordResetPage: React.FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [code, setCode] = useState<string>();
    const [username, setUsername] = useState<string>();
    const [brokenUrl, setBrokenUrl] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const {
        reset,
        resource,
        confirmUserResetPassword: confirmResetPassword,
    } = useResourceManagement({confirmUserResetPassword});

    const PASSWORD_RESET_SCHEMA = z
        .object({
            password: z.string().min(1),
            passwordConf: z.string().min(1),
        })
        .refine((data) => data.password === data.passwordConf, {
            message: t("password_reset_page.form.no_match"),
            path: ["passwordConf"],
        });

    type PasswordResetSchema = z.infer<typeof PASSWORD_RESET_SCHEMA>;

    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm<PasswordResetSchema>({
        defaultValues: {
            password: "",
            passwordConf: "",
        },
        resolver: zodResolver(PASSWORD_RESET_SCHEMA),
    });

    useEffect(() => {
        const code = searchParams.get("code");
        const username = searchParams.get("username");
        if (code && username) {
            setCode(code);
            setUsername(username);
        } else {
            setBrokenUrl(true);
        }
    }, [setBrokenUrl, setCode, setUsername, searchParams]);

    const onSubmit: SubmitHandler<PasswordResetSchema> = (data) => {
        reset();
        confirmResetPassword(username!, data.password, code!)
            .then(() => {
                navigate({
                    pathname: `/login`,
                    search: location.search,
                });
            })
            .catch(swallowException);
    };

    return (
        <AppLayout header={<EmptyHeader />}>
            <Grid container component="main" display="flex" justifyContent="center" alignItems="center" height="100%">
                <Grid
                    item
                    component={Paper}
                    elevation={0}
                    square
                    sx={{
                        minWidth: "31.25rem",
                        width: "31.25rem",
                        borderRadius: "1rem",
                    }}
                >
                    {isResourceFailed(resource) && (
                        <Alert severity="error" sx={{paddingX: "2.5rem"}}>
                            {getResourceErrorMessage(resource)}
                        </Alert>
                    )}
                    {brokenUrl && (
                        <Alert severity="error" sx={{paddingX: "2.5rem"}}>
                            {t("password_reset_page.broken")}
                        </Alert>
                    )}
                    <Stack
                        direction="column"
                        justifyContent="center"
                        sx={{
                            paddingY: "2rem",
                            paddingX: "3rem",
                        }}
                    >
                        <Stack direction="column" alignItems="center">
                            <Typography component="h1" variant="h5">
                                {t("password_reset_page.title")}
                            </Typography>
                            <Box sx={{marginTop: "1rem", paddingX: "1.25rem", width: "100%"}}>
                                <form>
                                    <LabeledInput label={t("password_reset_page.form.password")}>
                                        <TextInput
                                            name="password"
                                            type="password"
                                            register={register}
                                            errors={errors}
                                            inputProps={{autoFocus: true, autoComplete: "password"}}
                                        />
                                    </LabeledInput>
                                    <LabeledInput label={t("password_reset_page.form.password_conf")}>
                                        <TextInput
                                            name="passwordConf"
                                            type="password"
                                            register={register}
                                            errors={errors}
                                        />
                                    </LabeledInput>
                                    <ActionButton
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        icon="none"
                                        sx={{
                                            marginTop: "1.5rem",
                                            marginBottom: "1rem",
                                        }}
                                        onClick={handleSubmit(onSubmit)}
                                        disabled={isResourceLoading(resource) || brokenUrl}
                                    >
                                        {t("password_reset_page.form.submit_button")}
                                    </ActionButton>
                                </form>
                            </Box>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </AppLayout>
    );
};
